// ad-reward 플러그인 — 광고를 끝까지 보면 보상 (prd/112 Story 10, FR-016)
// 웹 보상형 광고는 광고사가 서버로 시청 완료를 알려주지 않는다. 그래서 MVP API 가 광고 직전에 1회용 티켓을 발급하고,
// 시청 뒤 그 티켓으로만 보상을 청구한다. 무엇을 줄지는 화면이 아니라 MVP API 가 정한다 (api/internal/entitlement 의 GrantAdReward)
//
// 처음에는 꺼져 있다 — countries 가 비어 있으면 버튼도 구글 스크립트도 없다. 켜는 순서 (README 「광고 보상」):
//   1. AdSense 콘솔에서 승인 사이트가 whif.io 루트인지 확인하고, 보상형 광고 단위를 만든다 (사람)
//   2. composition/plugin-config.ts 에 'ad-reward': { params: { slot: '<광고 단위>', countries: ['US'] } }
//   3. MVP 화면이 adGateStore.set(…) 으로 게이트 신호 넷을 넣는다 (ports/ad-gate.ts)
// 되돌릴 때는 countries 를 비운다
import { z } from 'zod';

import { adRewardSwitch, definePlugin } from '../../composition/plugin-runtime';
import { isConfigured } from './domain/gate';
import { rewardWidget } from './presentation/reward-widget';

/** 모든 MVP 가 whif 와 같은 AdSense 게시자 계정을 쓴다. 공개 식별자다 — https://www.whif.io/ads.txt 에 같은 값이 있다 */
const ADSENSE_CLIENT = 'ca-pub-5844226934846378';

export default definePlugin({
  name: 'ad-reward',
  version: '0.1.0',
  // 티켓은 로그인한 유저에게만 발급된다
  dependsOn: ['auth'],
  params: z.object({
    client: z.string().regex(/^(ca-pub-\d{16})?$/),
    /** AdSense 보상형 광고 단위. 비어 있으면 광고를 띄우지 않는다 */
    slot: z.string().regex(/^\d{0,20}$/),
    /** 광고를 여는 국가 (ISO 3166-1 두 글자 대문자). 비어 있으면 어디서도 띄우지 않는다 */
    countries: z.array(z.string().regex(/^[A-Z]{2}$/)),
    endpoint: z.string().startsWith('/'),
  }),
  defaults: { params: { client: ADSENSE_CLIENT, slot: '', countries: [], endpoint: '/api/reward-tickets' } },
  setup(ctx, params) {
    ctx.messages('ko', {
      label: '광고 보상',
      watch: '광고 보고 무료로 이용하기',
      'result.credited': '보상을 받았어요',
      'result.unavailable': '지금은 볼 수 있는 광고가 없어요. 잠시 뒤 다시 시도해 주세요',
      'result.failed': '보상을 받지 못했어요. 다시 시도해 주세요',
    });
    ctx.messages('en', {
      label: 'Ad reward',
      watch: 'Watch an ad to use for free',
      'result.credited': 'Reward received',
      'result.unavailable': 'No ad is available right now. Please try again shortly',
      'result.failed': 'Could not grant the reward. Please try again',
    });
    ctx.slot('home.main', rewardWidget(params), { priority: 30 });
    // 다른 플러그인에 「이 앱은 광고 보상을 쓴다」고 알린다 — llm 의 이용권 소진 안내가 광고를 말할지 정한다 (prd/123 Discovery)
    adRewardSwitch.set(isConfigured(params));
  },
  dispose() {
    adRewardSwitch.set(false);
  },
});
